import type { DatePickerValue, ServerSideSelectionState } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { useCallback, useMemo, useState } from 'react';
import type {
  BulkConstructionDatesResponse,
  BulkConstructionDatesSelection,
  BulkConstructionDatesUpdates,
  SummariesSearchPayload,
} from '~/api/intelligenceSummariesV3';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { IntelligenceSummary } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { aggregateBeforeSnapshots, getSelectionSnapshotRows } from './analytics';
import { useBulkUpdateConstructionDates } from './queries';

/** Map the table's selection state to the bulk endpoint's shape ('all' carries the table's own filters). */
export function toBulkSelection(
  selection: ServerSideSelectionState,
  buildSearchForBulk: () => SummariesSearchPayload,
): BulkConstructionDatesSelection {
  return selection.mode === 'all'
    ? { mode: 'all', excludedSummaryIds: selection.excludedIds, search: buildSearchForBulk() }
    : { mode: 'partial', summaryIds: selection.selectedIds };
}

/** Prefill values: exactly one selected row seeds the fields from it (homeViewV2 pattern); otherwise blank. */
export function getPrefillDates(
  selection: ServerSideSelectionState,
  getSummaryById: (id: string) => IntelligenceSummary | undefined,
): { constructionYear: string; constructionStartDate: string; constructionEndDate: string } {
  if (selection.mode === 'partial' && selection.selectedIds.length === 1) {
    const summary = getSummaryById(selection.selectedIds[0]);
    if (summary) {
      return {
        constructionYear: summary.project.constructionYear ? String(summary.project.constructionYear) : '',
        constructionStartDate: summary.project.constructionStartDate?.toISODate() ?? '',
        constructionEndDate: summary.project.constructionEndDate?.toISODate() ?? '',
      };
    }
  }
  return { constructionYear: '', constructionStartDate: '', constructionEndDate: '' };
}

/**
 * 'YYYY' string → Year-view DatePickerValue. Dec 31 local midnight matches
 * what the picker's Year view itself produces, so the round trip is stable.
 */
export function yearToDatePickerValue(year: string): DatePickerValue {
  const trimmed = year.trim();
  if (!/^\d{4}$/.test(trimmed)) return null;
  return { date: new Date(Number(trimmed), 11, 31).toISOString(), view: 'Year' };
}

/** Year-view DatePickerValue → 'YYYY' string (same format the numeric field produced). */
export function datePickerValueToYear(value: DatePickerValue): string {
  if (!value || !('date' in value) || typeof value.date !== 'string') return '';
  const date = new Date(value.date);
  return Number.isNaN(date.getTime()) ? '' : String(date.getFullYear());
}

/** Only set fields are sent; the year must be a 4-digit number. */
export function toUpdates(
  constructionYear: string,
  constructionStartDate: string,
  constructionEndDate: string,
): BulkConstructionDatesUpdates {
  const updates: BulkConstructionDatesUpdates = {};
  if (/^\d{4}$/.test(constructionYear.trim())) updates.constructionYear = Number(constructionYear.trim());
  if (constructionStartDate) updates.constructionStartDate = constructionStartDate;
  if (constructionEndDate) updates.constructionEndDate = constructionEndDate;
  return updates;
}

/** Warning copy for a partial recompute dispatch; null when every dispatch succeeded. */
export function getPartialDispatchWarning(result: BulkConstructionDatesResponse): string | null {
  if (result.dispatchFailedCount <= 0) return null;
  const plural = result.dispatchFailedCount === 1 ? '' : 's';
  return `Dates were saved, but conflict data for ${result.dispatchFailedCount} project${plural} could not be refreshed automatically.`;
}

/** Properties for the bulk-updated event, including dispatch coverage from the endpoint result. */
export function toBulkUpdatedEventProperties(
  organizationId: string,
  selectionMode: 'partial' | 'all',
  updates: BulkConstructionDatesUpdates,
  result: BulkConstructionDatesResponse,
): Record<string, unknown> {
  return {
    organization_id: organizationId,
    selection_mode: selectionMode,
    row_count: result.updatedCount,
    dispatched_count: result.dispatchedCount,
    dispatch_failed_count: result.dispatchFailedCount,
    set_year: updates.constructionYear != null,
    set_start: !!updates.constructionStartDate,
    set_end: !!updates.constructionEndDate,
  };
}

export type InlineDateField = 'year' | 'startDate' | 'endDate';

/** Local-midnight Date for a 'YYYY-MM-DD' string; undefined otherwise (e.g. a DatePicker minDate floor). */
export function toLocalDate(dateString: string): Date | undefined {
  const m = dateString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Single-field payload for an inline date-cell edit ('YYYY' for the year cell,
 * 'YYYY-MM-DD' otherwise); the server derives the rest per record. Empty when
 * the value is invalid.
 */
export function toInlineDateUpdates(field: InlineDateField, value: string): BulkConstructionDatesUpdates {
  if (field === 'year') return toUpdates(value, '', '');
  if (!value) return {};
  return field === 'startDate' ? { constructionStartDate: value } : { constructionEndDate: value };
}

/**
 * End date implied by a new start: the record's original duration is preserved
 * (6 months when the original pair is missing or inverted). Returns '' for an
 * invalid new start.
 */
export function deriveEndFromStart(newStart: string, prefillStart: string, prefillEnd: string): string {
  const start = DateTime.fromISO(newStart);
  if (!newStart || !start.isValid) return '';

  let duration: ReturnType<DateTime['diff']> | { months: number } = { months: 6 };
  if (prefillStart && prefillEnd) {
    const oldStart = DateTime.fromISO(prefillStart);
    const oldEnd = DateTime.fromISO(prefillEnd);
    if (oldStart.isValid && oldEnd.isValid && oldEnd >= oldStart) {
      duration = oldEnd.diff(oldStart);
    }
  }

  return start.plus(duration).toISODate() ?? '';
}

export type ConstructionDateFields = {
  constructionYear: string;
  constructionStartDate: string;
  constructionEndDate: string;
};

/**
 * Updates limited to fields the user changed from the prefill. An untouched
 * prefilled date must not be sent: an explicit date wins server-side and would
 * defeat the per-record derivation (e.g. year change → Jan 1 start with each
 * project's own duration preserved).
 */
export function getChangedUpdates(
  values: ConstructionDateFields,
  prefill: ConstructionDateFields,
): BulkConstructionDatesUpdates {
  return toUpdates(
    values.constructionYear !== prefill.constructionYear ? values.constructionYear : '',
    values.constructionStartDate !== prefill.constructionStartDate ? values.constructionStartDate : '',
    values.constructionEndDate !== prefill.constructionEndDate ? values.constructionEndDate : '',
  );
}

export type UseBulkEditConstructionDatesParams = {
  organizationId: string;
  /** Row lookup for the single-selection prefill (current page only, like homeViewV2). */
  getSummaryById: (id: string) => IntelligenceSummary | undefined;
  /** The table's current filters/sort, sent when the selection is select-all. */
  buildSearchForBulk: () => SummariesSearchPayload;
  /** Called after a successful apply with the endpoint result (poll + invalidate + clear selection). */
  onApplied: (result: BulkConstructionDatesResponse) => void;
};

/**
 * Dialog state + submit flow for the "Edit Construction Dates" bulk action,
 * modeled on homeViewV2's useBulkEditDates: opening with exactly one selected
 * row prefills the fields from it; only set fields are sent.
 */
export function useBulkEditConstructionDates({
  organizationId,
  getSummaryById,
  buildSearchForBulk,
  onApplied,
}: UseBulkEditConstructionDatesParams) {
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [prefill, setPrefill] = useState<ConstructionDateFields>({
    constructionYear: '',
    constructionStartDate: '',
    constructionEndDate: '',
  });
  const [constructionYear, setConstructionYear] = useState('');
  const [constructionStartDate, setConstructionStartDate] = useState('');
  const [constructionEndDate, setConstructionEndDate] = useState('');

  const mutation = useBulkUpdateConstructionDates(organizationId);

  const open = useCallback(
    (selection: ServerSideSelectionState) => {
      setPendingSelection(selection);
      const prefillValues = getPrefillDates(selection, getSummaryById);
      setPrefill(prefillValues);
      setConstructionYear(prefillValues.constructionYear);
      setConstructionStartDate(prefillValues.constructionStartDate);
      setConstructionEndDate(prefillValues.constructionEndDate);

      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryEditDatesDialogOpened, {
        organization_id: organizationId,
        selection_mode: selection.mode,
        selection_count: selection.mode === 'partial' ? selection.selectedIds.length : null,
        excluded_count: selection.mode === 'all' ? selection.excludedIds.length : 0,
        ...aggregateBeforeSnapshots(getSelectionSnapshotRows(selection, getSummaryById)),
      });
    },
    [getSummaryById, organizationId],
  );

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const isSingleSelection = pendingSelection?.mode === 'partial' && pendingSelection.selectedIds.length === 1;

  // Dragging the start moves the end with it (duration preserved); multi-row leaves derivation to the server.
  const onStartDateChange = useCallback(
    (value: string) => {
      setConstructionStartDate(value);
      if (!isSingleSelection) return;
      const end = deriveEndFromStart(value, prefill.constructionStartDate, prefill.constructionEndDate);
      if (end) setConstructionEndDate(end);
    },
    [isSingleSelection, prefill],
  );

  // Only changed fields are sent; the server derives unset dates per record.
  const updates = useMemo(
    () => getChangedUpdates({ constructionYear, constructionStartDate, constructionEndDate }, prefill),
    [constructionYear, constructionStartDate, constructionEndDate, prefill],
  );
  const canSubmit = Object.keys(updates).length > 0;

  const onSubmit = useCallback(() => {
    if (!organizationId || !pendingSelection) return;
    if (Object.keys(updates).length === 0) return;

    // Snapshot the selection's cached rows before the server applies the edit.
    const snapshotRows = getSelectionSnapshotRows(pendingSelection, getSummaryById);

    mutation.mutate(
      { selection: toBulkSelection(pendingSelection, buildSearchForBulk), updates },
      {
        onSuccess: (result) => {
          // Dates persisted even when some recompute dispatches failed; those
          // rows keep stale conflict data until the next recompute.
          const partialDispatchWarning = getPartialDispatchWarning(result);
          if (partialDispatchWarning) {
            Notify.warning({ message: partialDispatchWarning });
          }
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryConstructionDatesBulkUpdated, {
            ...toBulkUpdatedEventProperties(organizationId, pendingSelection.mode, updates, result),
            date_edit_id: nanoid(),
            project_ids: snapshotRows.map((s) => s.project.id),
            ...aggregateBeforeSnapshots(snapshotRows),
          });
          setPendingSelection(null);
          onApplied(result);
        },
        // The dialog stays open on failure so the user can retry.
        onError: (err) => {
          Notify.error({
            message: 'An error occurred while updating construction dates. Please contact support.',
          });
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryConstructionDatesBulkUpdateFailed, {
            organization_id: organizationId,
            selection_mode: pendingSelection.mode,
            error_message: err instanceof Error ? err.message : 'unknown',
          });
        },
      },
    );
  }, [organizationId, pendingSelection, updates, buildSearchForBulk, mutation, onApplied, getSummaryById]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    constructionYear,
    setConstructionYear,
    constructionStartDate,
    setConstructionStartDate: onStartDateChange,
    constructionEndDate,
    setConstructionEndDate,
    canSubmit,
    onSubmit,
    isPending: mutation.isPending,
  };
}
